import React, { useEffect, useState, useMemo, useCallback } from "react";
import { db } from "../../firebase";
import {
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { useBuilding } from "../../context/BuildingContext";
import { useToast } from "../../components/Toast";
import { formatCurrencyUY, formatCurrencyUSD } from "../../utils/formatters";
import { tolerantId } from "../../utils/firestoreHelpers";
import { useLatestRate } from "../../utils/exchangeRates";
import { buildAccountMovements, computeCurrentFundBalances, saldoTotalDeFondos } from "../../utils/fundMovements";
import { resolveCierreWindow } from "../../utils/cierrePeriod";
import { groupFundsByBankAccount } from "../../utils/fundHelpers";
import {
  colors,
  shadows,
  radius,
  spacing,
  typography,
  transitions,
  cardBase,
  btnPrimary,
  btnSecondary,
  btnDanger,
  btnGhost,
  inputBase,
  pageContainer,
  pageTitle,
  tableHeader,
  tableCell,
  modalOverlay,
} from "../../utils/designTokens";
import {
  FaChartBar,
  FaBuilding,
  FaCalendarAlt,
  FaFileExcel,
  FaFilePdf,
  FaUniversity,
} from "react-icons/fa";
import KPICard from "../../components/KPICard";
import KpiInfoTooltip from "../../components/KpiInfoTooltip";
import LoadingState from "../../components/LoadingState";
import { getKpiBadgeColor } from "../../utils/kpiDefinitions";
import { calculateReserveMonths } from "../../utils/kpiCalculations";
import logoImg from "../../assets/logo.png";
import { loadImageForPDF } from "../../utils/pdfAssets";

const MONTHS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

// Tabs base (built-in); los fondos personalizados se agregan dinámicamente
const BASE_FUND_TABS = [
  { key: "caja", label: "Caja", color: colors.primary },
  { key: "reserva", label: "Reserva", color: "#8b5cf6" },
];

const EstadoCuentaEdificio = () => {
  const { showToast } = useToast();
  const { selectedBuilding } = useBuilding();
  const { rate: bcuRate } = useLatestRate();
  const buildingId = selectedBuilding?.buildingid;

  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [activeFund, setActiveFund] = useState("caja");

  // Raw data
  const [expenses, setExpenses] = useState([]);
  const [allPayments, setAllPayments] = useState([]);
  const [multas, setMultas] = useState([]);
  const [incomes, setIncomes] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [buildingData, setBuildingData] = useState({});

  // Separar payments: pagos de gastos (tienen expensesid) vs cobros de residentes (tienen userid+month)
  const expensePayments = useMemo(
    () => allPayments.filter((p) => p.expensesid != null),
    [allPayments]
  );
  const residentPayments = useMemo(
    () => allPayments.filter((p) => p.userid && (p.month != null)),
    [allPayments]
  );

  useEffect(() => {
    if (!buildingId) return;
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [expSnap, paySnap, paySnap2, multSnap, incSnap, aptSnap, bldSnap, supSnap] = await Promise.all([
        getDocs(query(collection(db, "expenses"), where("buildingid", "in", tolerantId(buildingId)))),
        // FIN-053: Payments usan "buildingid" (lowercase, estandar) y legacy "buildingId" (camelCase)
        // Se hacen 2 queries y se deduplican por doc ID para cubrir ambos
        getDocs(query(collection(db, "payments"), where("buildingid", "in", tolerantId(buildingId)))),
        getDocs(query(collection(db, "payments"), where("buildingId", "in", tolerantId(buildingId)))),
        getDocs(query(collection(db, "multas"), where("buildingid", "in", tolerantId(buildingId)))),
        getDocs(query(collection(db, "buildingincomes"), where("buildingid", "in", tolerantId(buildingId)))),
        getDocs(query(collection(db, "apartments"), where("building", "in", tolerantId(buildingId)))),
        getDocs(query(collection(db, "building"), where("buildingid", "in", tolerantId(buildingId)))),
        getDocs(collection(db, "supplier")),
      ]);

      setExpenses(expSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      // Merge y deduplicar payments por doc ID
      const payMap = new Map();
      paySnap.docs.forEach((d) => payMap.set(d.id, { id: d.id, ...d.data() }));
      paySnap2.docs.forEach((d) => { if (!payMap.has(d.id)) payMap.set(d.id, { id: d.id, ...d.data() }); });
      setAllPayments(Array.from(payMap.values()));

      setMultas(multSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setIncomes(incSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setApartments(aptSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setBuildingData(bldSnap.docs[0]?.data() || {});
      setSuppliers(supSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Error fetching data:", err);
      showToast("Error al cargar datos", "error");
    } finally {
      setLoading(false);
    }
  };

  // Saldos actuales usando flujo de caja (misma lógica que la tabla de movimientos)
  const currentBalances = useMemo(
    () => computeCurrentFundBalances({ expenses, expensePayments, residentPayments, multas, incomes, building: buildingData, currency: "peso" }),
    [expenses, expensePayments, residentPayments, multas, incomes, buildingData]
  );
  const hasUSD = useMemo(
    () => expenses.some((e) => (e.money || "").toLowerCase() === "dolar") || incomes.some((i) => (i.currency || "").toUpperCase() === "USD"),
    [expenses, incomes]
  );
  // FIN-030: Saldos USD y total consolidado multi-moneda
  const currentBalancesUSD = useMemo(
    () => hasUSD ? computeCurrentFundBalances({ expenses, expensePayments, residentPayments, multas, incomes, building: buildingData, currency: "dolar" }) : null,
    [expenses, expensePayments, residentPayments, multas, incomes, buildingData, hasUSD]
  );
  // TC: prioridad al override manual del edificio, fallback al BCU
  const manualRate = Number(buildingData?.exchangeRateUSD) || 0;
  const exchangeRate = manualRate > 0 ? manualRate : (bcuRate?.sell || 0);
  const exchangeRateSource = manualRate > 0 ? "Manual" : (bcuRate ? "BCU" : "—");
  const consolidatedTotal = useMemo(() => {
    if (!hasUSD || exchangeRate <= 0 || !currentBalancesUSD) return null;
    const uyuTotal = (currentBalances.caja || 0) + (currentBalances.reserva || 0);
    const usdTotal = (currentBalancesUSD.caja || 0) + (currentBalancesUSD.reserva || 0);
    return uyuTotal + (usdTotal * exchangeRate);
  }, [currentBalances, currentBalancesUSD, hasUSD, exchangeRate]);

  // Tabs visibles: fondos built-in + personalizados + USD
  const visibleTabs = useMemo(() => {
    const tabs = [...BASE_FUND_TABS];
    // Agregar fondos personalizados activos del edificio
    (buildingData?.customFunds || []).filter(f => f.active !== false).forEach(f => {
      tabs.push({ key: f.id, label: f.name, color: f.color || "#e67e22" });
    });
    if (hasUSD) tabs.push({ key: "usd", label: "USD", color: colors.success });
    return tabs;
  }, [hasUSD, buildingData]);

  // Ventana del período (fecha de corte configurable). Con día de corte, los
  // movimientos del mes se bucketean por la ventana (ej. septiembre = 05/09→05/10).
  const diaCorte = buildingData?.diaCorte ?? null;
  const periodWindow = useMemo(() => resolveCierreWindow(month, year, { diaCorte }), [month, year, diaCorte]);

  // Movimientos del fondo activo
  const movementData = useMemo(() => {
    const isUsdTab = activeFund === "usd";
    return buildAccountMovements({
      expenses,
      expensePayments,
      residentPayments,
      multas,
      incomes,
      suppliers,
      apartments,
      building: buildingData,
      year,
      month,
      fund: isUsdTab ? "caja" : activeFund,
      currency: isUsdTab ? "dolar" : "peso",
      periodStart: diaCorte ? periodWindow.start : null,
      periodEnd: diaCorte ? periodWindow.end : null,
    });
  }, [expenses, expensePayments, residentPayments, multas, incomes, suppliers, apartments, buildingData, year, month, activeFund, diaCorte, periodWindow]);

  const fmt = activeFund === "usd" ? (v) => formatCurrencyUSD(v) : (v) => formatCurrencyUY(v);
  const fmtDate = (d) => {
    if (!d) return "—";
    const date = d instanceof Date ? d : new Date(d);
    return date.toLocaleDateString("es-UY", { day: "2-digit", month: "2-digit" });
  };

  // --- Export PDF ---
  const exportPDF = useCallback(async () => {
    const { default: jsPDF } = await import("jspdf");
    const { default: autoTable } = await import("jspdf-autotable");
    const doc = new jsPDF();
    const pw = doc.internal.pageSize.getWidth(); // 210
    const ph = doc.internal.pageSize.getHeight(); // 297
    const mg = 18;
    const contentW = pw - mg * 2;
    const tabLabel = visibleTabs.find((t) => t.key === activeFund)?.label || activeFund;
    const currLabel = activeFund === "usd" ? "USD" : "UYU";
    const periodo = `${MONTHS[month - 1]} ${year}`;
    const bName = selectedBuilding?.name || "Edificio";

    const fmtV = (v) => {
      if (v == null) return "";
      return activeFund === "usd"
        ? `US$ ${v.toLocaleString("es-UY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : `$ ${Math.round(v).toLocaleString("es-UY")}`;
    };
    const fmtD = (d) => {
      if (!d) return "";
      const dt = d instanceof Date ? d : new Date(d);
      return dt.toLocaleDateString("es-UY", { day: "2-digit", month: "short" });
    };

    let y = mg;

    // ======================
    // LOGO (PNG con transparencia, dimensiones proporcionales reales)
    // logo.png = 3369x1493 → ratio 2.26:1
    // ======================
    let logoH = 0;
    try {
      const logoResult = await loadImageForPDF(logoImg, 300);
      if (logoResult) {
        const logoW = 34;
        logoH = logoW / logoResult.ratio; // ~15mm
        doc.addImage(logoResult.data, "PNG", mg, y, logoW, logoH);
      }
    } catch (_) {}

    // Fecha de emision (derecha, centrada verticalmente con logo)
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(156, 163, 175);
    doc.text(`Emitido: ${new Date().toLocaleDateString("es-UY")}`, pw - mg, y + (logoH || 10) / 2 + 1, { align: "right" });

    y += (logoH || 10) + 4;

    // ======================
    // TITULO + INFO
    // ======================
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(17, 24, 39);
    doc.text("Estado de Cuenta", mg, y);
    y += 7;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(107, 114, 128);
    doc.text(`${bName}  ·  ${tabLabel} (${currLabel})  ·  ${periodo}`, mg, y);
    y += 6;

    // Linea separadora fina
    doc.setDrawColor(0, 128, 255);
    doc.setLineWidth(0.8);
    doc.line(mg, y, mg + 30, y); // solo un trazo corto azul (acento)
    doc.setDrawColor(229, 231, 235);
    doc.setLineWidth(0.2);
    doc.line(mg + 30, y, pw - mg, y); // el resto gris
    y += 8;

    // ======================
    // RESUMEN (banda horizontal con 4 columnas)
    // ======================
    const bandH = 24;
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(mg, y, contentW, bandH, 2, 2, "F");

    const colW = contentW / 4;
    const summaryItems = [
      { label: "Saldo anterior", value: fmtV(movementData.saldoInicial), color: [55, 65, 81] },
      { label: "Gastos", value: `- ${fmtV(movementData.totalDebitos)}`, color: [220, 38, 38] },
      { label: "Cobranza", value: `+ ${fmtV(movementData.totalCreditos)}`, color: [22, 163, 74] },
      { label: "Saldo final", value: fmtV(movementData.saldoFinal),
        color: movementData.saldoFinal >= 0 ? [0, 128, 255] : [220, 38, 38] },
    ];

    summaryItems.forEach((item, i) => {
      const cx = mg + colW * i + colW / 2;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(156, 163, 175);
      doc.text(item.label.toUpperCase(), cx, y + 8, { align: "center" });

      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(...item.color);
      doc.text(item.value, cx, y + 17, { align: "center" });

      // Separador vertical entre columnas
      if (i < 3) {
        doc.setDrawColor(229, 231, 235);
        doc.setLineWidth(0.2);
        doc.line(mg + colW * (i + 1), y + 4, mg + colW * (i + 1), y + bandH - 4);
      }
    });

    y += bandH + 10;

    // ======================
    // TABLA DE MOVIMIENTOS
    // ======================
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(17, 24, 39);
    doc.text("Detalle de movimientos", mg, y);
    y += 5;

    // Construir filas: saldo inicial + movimientos + saldo final
    const SALDO_INI_TAG = "__SALDO_INI__";
    const SALDO_FIN_TAG = "__SALDO_FIN__";
    const rows = [];

    // Fila de saldo inicial
    rows.push(["", "", "Saldo inicial", "", "", fmtV(movementData.saldoInicial), SALDO_INI_TAG]);

    // Movimientos
    movementData.movements.forEach((mv) => {
      const concepto = mv.ref
        ? `${mv.description}  (${mv.ref})`
        : mv.description;
      rows.push([
        fmtD(mv.date),
        mv.type,
        concepto,
        mv.debit ? fmtV(mv.debit) : "",
        mv.credit ? fmtV(mv.credit) : "",
        fmtV(mv.balance),
        null,
      ]);
    });

    // Fila de saldo final
    rows.push(["", "", "Saldo final", "", "", fmtV(movementData.saldoFinal), SALDO_FIN_TAG]);

    autoTable(doc, {
      startY: y,
      head: [["Fecha", "Tipo", "Concepto", "Gastos", "Cobranza", "Saldo"]],
      body: rows.map((r) => r.slice(0, 6)), // excluir columna tag interna
      theme: "striped",
      styles: {
        fontSize: 8,
        cellPadding: { top: 4, bottom: 4, left: 4, right: 4 },
        textColor: [55, 65, 81],
        lineColor: [241, 245, 249],
        lineWidth: 0,
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [107, 114, 128],
        fontStyle: "bold",
        fontSize: 7.5,
        cellPadding: { top: 3, bottom: 6, left: 4, right: 4 },
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252],
      },
      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 22, fontStyle: "bold", fontSize: 7 },
        2: { cellWidth: "auto", fontSize: 8 },
        3: { halign: "right", cellWidth: 24 },
        4: { halign: "right", cellWidth: 24 },
        5: { halign: "right", cellWidth: 26, fontStyle: "bold" },
      },
      didParseCell: (data) => {
        if (data.section === "head") {
          data.cell.styles.lineWidth = { bottom: 0.5 };
          data.cell.styles.lineColor = [0, 128, 255];
        }
        if (data.section !== "body") return;

        // Filas de saldo inicial y final: fondo especial y negrita
        const tag = rows[data.row.index]?.[6];
        if (tag === SALDO_INI_TAG || tag === SALDO_FIN_TAG) {
          data.cell.styles.fillColor = [240, 245, 255];
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.textColor = [30, 58, 138];
          return;
        }

        // Tipo: colores por categoria
        if (data.column.index === 1) {
          const t = String(rows[data.row.index]?.[1] || "");
          if (t.includes("GASTO")) data.cell.styles.textColor = [220, 38, 38];
          else if (t.includes("COBRANZA") || t.includes("MULTA")) data.cell.styles.textColor = [22, 163, 74];
          else data.cell.styles.textColor = [107, 114, 128];
        }
        // Debito en rojo
        if (data.column.index === 3 && rows[data.row.index]?.[3]) {
          data.cell.styles.textColor = [220, 38, 38];
        }
        // Credito en verde
        if (data.column.index === 4 && rows[data.row.index]?.[4]) {
          data.cell.styles.textColor = [22, 163, 74];
        }
        // Saldo: rojo si negativo
        if (data.column.index === 5) {
          if (String(rows[data.row.index]?.[5] || "").includes("-")) {
            data.cell.styles.textColor = [220, 38, 38];
          }
        }
      },
      margin: { left: mg, right: mg },
    });

    // ======================
    // FOOTER
    // ======================
    doc.setDrawColor(229, 231, 235);
    doc.setLineWidth(0.2);
    doc.line(mg, ph - 14, pw - mg, ph - 14);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(156, 163, 175);
    doc.text("habitia.app", mg, ph - 9);
    doc.text(
      `${bName}  ·  ${tabLabel} (${currLabel})  ·  ${periodo}`,
      pw - mg, ph - 9, { align: "right" }
    );

    const fileName = bName.replace(/\s+/g, "_");
    doc.save(`estado_cuenta_${fileName}_${tabLabel}_${MONTHS[month - 1]}_${year}.pdf`);
    showToast("PDF exportado", "success");
  }, [movementData, activeFund, month, year, selectedBuilding, visibleTabs, showToast]);

  // --- Export Excel ---
  const exportExcel = useCallback(async () => {
    const XLSX = await import("xlsx");
    const tabLabel = visibleTabs.find((t) => t.key === activeFund)?.label || activeFund;
    const rows = [];

    // Header row
    rows.push(["Fecha", "Referencia", "Tipo", "Descripcion", "Gastos", "Cobranza", "Saldo"]);
    rows.push(["", "", "", "Saldo inicial", "", "", movementData.saldoInicial]);

    movementData.movements.forEach((m) => {
      rows.push([
        m.date ? m.date.toLocaleDateString("es-UY") : "",
        m.ref,
        m.type,
        m.description,
        m.debit || "",
        m.credit || "",
        m.balance,
      ]);
    });

    rows.push(["", "", "", "Saldo final", "", "", movementData.saldoFinal]);
    rows.push([]);
    rows.push(["", "", "", "Total Gastos", movementData.totalDebitos, "", ""]);
    rows.push(["", "", "", "Total Cobranza", "", movementData.totalCreditos, ""]);
    rows.push(["", "", "", "Neto", "", "", movementData.totalCreditos - movementData.totalDebitos]);

    const ws = XLSX.utils.aoa_to_sheet(rows);

    // Ancho de columnas
    ws["!cols"] = [
      { wch: 12 }, { wch: 14 }, { wch: 16 }, { wch: 40 }, { wch: 14 }, { wch: 14 }, { wch: 14 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, tabLabel);

    const buildingName = (selectedBuilding?.name || "edificio").replace(/\s+/g, "_");
    XLSX.writeFile(wb, `estado_cuenta_${buildingName}_${tabLabel}_${MONTHS[month - 1]}_${year}.xlsx`);
    showToast("Excel exportado", "success");
  }, [movementData, activeFund, month, year, selectedBuilding, visibleTabs, showToast]);

  // --- Render ---

  if (!buildingId) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>
          <FaBuilding size={48} color={colors.gray400} />
          <p style={{ color: colors.gray600, marginTop: 16 }}>Selecciona un edificio</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}><FaChartBar style={{ marginRight: 8 }} /> Estado de Cuenta</h2>
          <p style={styles.subtitle}>{selectedBuilding?.name || "Edificio"}</p>
        </div>
      </div>

      {/* KPI cards */}
      {!loading && (
        <div style={styles.kpiRow}>
          <div style={styles.kpiCard}>
            <div style={{ ...styles.kpiValue, color: currentBalances.caja >= 0 ? colors.primary : colors.error }}>
              {formatCurrencyUY(currentBalances.caja)}
            </div>
            <div style={styles.kpiLabel}>Saldo Caja</div>
          </div>
          <div style={{ ...styles.kpiCard, borderLeftColor: colors.info }}>
            <div style={{ ...styles.kpiValue, color: currentBalances.reserva >= 0 ? colors.info : colors.error }}>
              {formatCurrencyUY(currentBalances.reserva)}
            </div>
            <div style={styles.kpiLabel}>Saldo Reserva</div>
          </div>
          {/* KPI 7: Meses de Reserva */}
          {(() => {
            const avgExpenses = movementData.totalDebitos > 0 ? movementData.totalDebitos : 1;
            const reserveResult = calculateReserveMonths(Math.max(0, currentBalances.reserva || 0), avgExpenses);
            return (
              <div style={{ position: "relative" }}>
                <div style={{ ...styles.kpiCard, borderLeftColor: colors.success }}>
                  <div style={{ ...styles.kpiValue, color: reserveResult.months >= 2 ? colors.success : reserveResult.months >= 1 ? colors.warning : colors.error }}>
                    {reserveResult.months}
                  </div>
                  <div style={styles.kpiLabel}>Meses de Reserva</div>
                </div>
                <div style={{ position: "absolute", top: 8, right: 8 }}>
                  <KpiInfoTooltip kpiKey="meses_reserva" />
                </div>
              </div>
            );
          })()}
          {/* KPI cards para fondos personalizados */}
          {(buildingData?.customFunds || []).filter(f => f.active !== false).map(f => {
            const bal = currentBalances[f.id] || 0;
            const fundColor = f.color || "#e67e22";
            return (
              <div key={f.id} style={{ ...styles.kpiCard, borderLeftColor: fundColor }}>
                <div style={{ ...styles.kpiValue, color: bal >= 0 ? fundColor : colors.error }}>
                  {formatCurrencyUY(bal)}
                </div>
                <div style={styles.kpiLabel}>{f.name}</div>
              </div>
            );
          })}
          <div style={{ ...styles.kpiCard, borderLeftColor: colors.success }}>
            <div style={{ ...styles.kpiValue, color: colors.success }}>{formatCurrencyUY(movementData.totalCreditos)}</div>
            <div style={styles.kpiLabel}>Cobranza del mes</div>
          </div>
          {/* Saldo total: la suma de las cuentas (caja + reserva + fondos propios) */}
          {(() => {
            const total = saldoTotalDeFondos(currentBalances, buildingData?.customFunds);
            return (
              <div style={{ ...styles.kpiCard, borderLeftColor: colors.primaryDark }}>
                <div style={{ ...styles.kpiValue, color: total >= 0 ? colors.primary : colors.error }}>{formatCurrencyUY(total)}</div>
                <div style={styles.kpiLabel}>Saldo total</div>
              </div>
            );
          })()}
          {/* FIN-030: KPI consolidado multi-moneda */}
          {consolidatedTotal != null && (
            <div style={{ ...styles.kpiCard, borderLeftColor: colors.warning }}>
              <div style={{ ...styles.kpiValue, color: consolidatedTotal >= 0 ? colors.warning : colors.error }}>
                {formatCurrencyUY(consolidatedTotal)}
              </div>
              <div style={styles.kpiLabel}>Total Consolidado (UYU)</div>
              <div style={{ fontSize: 10, color: colors.gray400, marginTop: 2 }}>TC: ${exchangeRate.toFixed(2)} ({exchangeRateSource})</div>
            </div>
          )}
        </div>
      )}

      {/* F4: Saldo bancario consolidado — solo si ≥1 cuenta es compartida por varios fondos */}
      {!loading && (() => {
        const { groups, hasSharedAccount } = groupFundsByBankAccount(buildingData);
        if (!hasSharedAccount) return null;
        const sharedGroups = groups.filter((g) => g.shared);
        const accLabel = (acc) => acc
          ? `${acc.bank || "Cuenta"}${acc.accountNumber ? ` ····${String(acc.accountNumber).slice(-4)}` : ""}${acc.accountType ? ` (${acc.accountType})` : ""}`
          : "Cuenta bancaria";
        return (
          <div style={{ ...styles.kpiCard, borderLeftColor: colors.warning, marginBottom: 20, padding: "16px 20px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <FaChartBar size={14} color={colors.warning} />
              <span style={{ fontSize: 14, color: colors.gray900 }}>Saldo bancario consolidado</span>
            </div>
            <p style={{ fontSize: 12, color: colors.gray500, margin: "0 0 12px" }}>
              Estos fondos comparten una misma cuenta bancaria. El saldo físico de la cuenta es la suma de los saldos contables (la segregación entre fondos se mantiene contable).
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
              {sharedGroups.map((g) => {
                const total = g.funds.reduce((s, f) => s + (currentBalances[f.id] || 0), 0);
                return (
                  <div key={g.accountId} style={{ background: colors.gray50, borderRadius: 8, padding: "12px 16px", minWidth: 220 }}>
                    <div style={{ fontSize: 12, color: colors.gray600, marginBottom: 4 }}>{accLabel(g.account)}</div>
                    <div style={{ fontSize: 20, color: total >= 0 ? colors.warning : colors.error }}>{formatCurrencyUY(total)}</div>
                    <div style={{ fontSize: 11, color: colors.gray400, marginTop: 6, display: "flex", flexDirection: "column", gap: 2 }}>
                      {g.funds.map((f) => (
                        <span key={f.id}>{f.name}: {formatCurrencyUY(currentBalances[f.id] || 0)}</span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Period selector + export buttons */}
      <div style={styles.filterRow}>
        <div style={styles.periodRow}>
          <FaCalendarAlt size={14} color={colors.gray600} />
          <select style={styles.periodSelect} value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <input
            type="number"
            style={{ ...styles.periodSelect, width: 80 }}
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            min={2020}
            max={2100}
          />
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={styles.exportBtn} onClick={exportPDF} title="Exportar PDF">
            <FaFilePdf size={14} /> PDF
          </button>
          <button style={{ ...styles.exportBtn, background: colors.success }} onClick={exportExcel} title="Exportar Excel">
            <FaFileExcel size={14} /> Excel
          </button>
        </div>
      </div>

      {/* Fund tabs */}
      <div style={styles.tabRow}>
        {visibleTabs.map((tab) => (
          <button
            key={tab.key}
            style={activeFund === tab.key ? { ...styles.tab, ...styles.tabActive, borderBottomColor: tab.color, color: tab.color } : styles.tab}
            onClick={() => setActiveFund(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Movement table */}
      {loading ? (
        <LoadingState variant="block" message="Cargando…" />
      ) : (
        <div style={styles.tableCard}>
          <div style={{ overflowX: "auto" }}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHeadRow}>
                  <th style={styles.th}>Fecha</th>
                  <th style={styles.th}>Ref</th>
                  <th style={styles.th}>Tipo</th>
                  <th style={styles.th}>Descripcion</th>
                  <th style={styles.thRight}>Gastos</th>
                  <th style={styles.thRight}>Cobranza</th>
                  <th style={styles.thRight}>Saldo</th>
                </tr>
              </thead>
              <tbody>
                {/* Saldo inicial */}
                <tr style={styles.saldoRow}>
                  <td style={styles.td}></td>
                  <td style={styles.td}></td>
                  <td style={styles.td}></td>
                  <td style={{ ...styles.td, fontWeight: 400, color: colors.gray900 }}>Saldo inicial</td>
                  <td style={styles.tdRight}></td>
                  <td style={styles.tdRight}></td>
                  <td style={{ ...styles.tdRight, fontWeight: 400, color: colors.primary }}>
                    {fmt(movementData.saldoInicial)}
                  </td>
                </tr>

                {/* Movimientos */}
                {movementData.movements.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ ...styles.td, textAlign: "center", padding: "24px 14px", color: colors.gray400 }}>
                      Sin movimientos en este periodo
                    </td>
                  </tr>
                ) : (
                  movementData.movements.map((m, i) => (
                    <tr key={i} style={i % 2 === 0 ? styles.trEven : styles.trOdd}>
                      <td style={styles.td}>{fmtDate(m.date)}</td>
                      <td style={{ ...styles.td, fontFamily: typography.monoFamily, fontSize: typography.sizes.sm }}>{m.ref}</td>
                      <td style={styles.td}>
                        <span style={m.debit ? styles.badgeDebit : styles.badgeCredit}>
                          {m.type}
                        </span>
                      </td>
                      <td style={{ ...styles.td, maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis" }}>{m.description}</td>
                      <td style={{ ...styles.tdRight, color: m.debit ? colors.error : undefined }}>
                        {m.debit ? `-${fmt(m.debit)}` : ""}
                      </td>
                      <td style={{ ...styles.tdRight, color: m.credit ? colors.success : undefined }}>
                        {m.credit ? `+${fmt(m.credit)}` : ""}
                      </td>
                      <td style={{ ...styles.tdRight, fontWeight: 400, color: m.balance >= 0 ? colors.black : colors.error }}>
                        {fmt(m.balance)}
                      </td>
                    </tr>
                  ))
                )}

                {/* Saldo final */}
                <tr style={styles.saldoRow}>
                  <td style={styles.td}></td>
                  <td style={styles.td}></td>
                  <td style={styles.td}></td>
                  <td style={{ ...styles.td, fontWeight: 400, color: colors.gray900 }}>Saldo final</td>
                  <td style={styles.tdRight}></td>
                  <td style={styles.tdRight}></td>
                  <td style={{ ...styles.tdRight, fontWeight: 400, color: movementData.saldoFinal >= 0 ? colors.primary : colors.error }}>
                    {fmt(movementData.saldoFinal)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Totals bar */}
          <div style={styles.totalsBar}>
            <span>
              Gastos: <strong style={{ color: colors.error }}>-{fmt(movementData.totalDebitos)}</strong>
            </span>
            <span>
              Cobranza: <strong style={{ color: colors.success }}>+{fmt(movementData.totalCreditos)}</strong>
            </span>
            <span>
              Neto: <strong style={{ color: movementData.totalCreditos - movementData.totalDebitos >= 0 ? colors.success : colors.error }}>
                {movementData.totalCreditos - movementData.totalDebitos >= 0 ? "+" : ""}{fmt(movementData.totalCreditos - movementData.totalDebitos)}
              </strong>
            </span>
            <span style={{ color: colors.gray400 }}>
              {movementData.movements.length} movimiento{movementData.movements.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Estilos con design tokens ---

const styles = {
  container: {
    padding: spacing[6],
    fontFamily: typography.fontFamily,
    backgroundColor: colors.bgMain,
    minHeight: "100vh",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: spacing[5],
    flexWrap: "wrap",
    gap: spacing[3],
  },
  title: {
    ...pageTitle,
    fontSize: typography.sizes.xl,
    display: "flex",
    alignItems: "center",
  },
  subtitle: {
    fontSize: typography.sizes.base,
    color: colors.gray600,
    margin: "4px 0 0",
  },
  kpiRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: spacing[3],
    marginBottom: spacing[5],
  },
  kpiCard: {
    background: colors.white,
    borderRadius: radius.sm,
    padding: `${spacing[4]}px ${spacing[5]}px`,
    borderLeft: `4px solid ${colors.primary}`,
    boxShadow: shadows.sm,
  },
  kpiValue: {
    fontSize: typography.sizes["2xl"],
    fontWeight: 400,
    color: colors.primary,
  },
  kpiLabel: {
    fontSize: typography.sizes.sm,
    color: colors.gray600,
    marginTop: 2,
  },
  filterRow: {
    display: "flex",
    gap: spacing[3],
    marginBottom: spacing[4],
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
  },
  periodRow: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    padding: `${spacing[2]}px ${spacing[4]}px`,
    background: colors.white,
    borderRadius: radius.sm,
    boxShadow: shadows.sm,
  },
  periodSelect: {
    padding: "6px 10px",
    border: `1px solid ${colors.gray300}`,
    borderRadius: radius.sm,
    fontSize: typography.sizes.base,
    fontFamily: typography.fontFamily,
    outline: "none",
    background: colors.white,
  },
  exportBtn: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: `${spacing[2]}px ${spacing[4]}px`,
    background: colors.error,
    color: colors.white,
    border: "none",
    borderRadius: radius.sm,
    fontSize: typography.sizes.base,
    fontWeight: 400,
    fontFamily: typography.fontFamily,
    cursor: "pointer",
    transition: `all ${transitions.fast}`,
  },
  tabRow: {
    display: "flex",
    gap: 0,
    marginBottom: 0,
    borderBottom: `2px solid ${colors.gray200}`,
  },
  tab: {
    padding: `${spacing[3]}px ${spacing[5]}px`,
    fontSize: typography.sizes.base,
    fontWeight: 400,
    fontFamily: typography.fontFamily,
    color: colors.gray600,
    background: "transparent",
    border: "none",
    borderBottom: "2px solid transparent",
    marginBottom: -2,
    cursor: "pointer",
    transition: `all ${transitions.fast}`,
  },
  tabActive: {
    color: colors.primary,
    borderBottom: `2px solid ${colors.primary}`,
  },
  tableCard: {
    background: colors.white,
    borderRadius: radius.md,
    overflow: "hidden",
    boxShadow: shadows.sm,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: typography.sizes.base,
  },
  // Encabezado de tabla azul (normativa de diseño)
  tableHeadRow: {
    background: colors.primary,
  },
  th: {
    ...tableHeader,
    whiteSpace: "nowrap",
  },
  thRight: {
    ...tableHeader,
    textAlign: "right",
    whiteSpace: "nowrap",
  },
  trEven: { background: colors.white },
  trOdd: { background: colors.gray50 },
  saldoRow: {
    background: colors.gray100,
    borderTop: `1px solid ${colors.gray200}`,
    borderBottom: `1px solid ${colors.gray200}`,
  },
  td: {
    ...tableCell,
    padding: `${spacing[2]}px ${spacing[4]}px`,
    whiteSpace: "nowrap",
  },
  tdRight: {
    ...tableCell,
    padding: `${spacing[2]}px ${spacing[4]}px`,
    textAlign: "right",
    whiteSpace: "nowrap",
  },
  badgeDebit: {
    display: "inline-block",
    padding: "2px 8px",
    background: colors.errorLight,
    color: colors.errorDark,
    borderRadius: radius.sm,
    fontSize: typography.sizes.xs,
    fontWeight: 400,
  },
  badgeCredit: {
    display: "inline-block",
    padding: "2px 8px",
    background: colors.successLight,
    color: colors.success,
    borderRadius: radius.sm,
    fontSize: typography.sizes.xs,
    fontWeight: 400,
  },
  totalsBar: {
    display: "flex",
    gap: spacing[6],
    padding: `${spacing[3]}px ${spacing[4]}px`,
    background: colors.gray50,
    borderTop: `1px solid ${colors.gray200}`,
    fontSize: typography.sizes.base,
    color: colors.gray900,
    flexWrap: "wrap",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "60px 20px",
  },
};

export default EstadoCuentaEdificio;
